import React, { useEffect, useState } from 'react';

import './BdsInput.scss';
import { IBdsInput } from '../../../interfaces/bdsInput';

const BdsInput = ({
  labelText,
  hintText,
  helperText,
  prefixIcon,
  suffixIcon,
  maxLength,
  initValue = '',
  enabled = true,
  type = 'text',
  name,
  autoComplete = 'off',
  onChanged,
  onSubmitPressed,
  onBlur,
  onSuffixIconClick,
  validator,
  inputFormatters,
  resetState,
  focusedError,
  className
}: IBdsInput) => {
  const [value, setValue] = useState<string>(initValue);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (inputFormatters) {
      inputFormatters.forEach((formatter) => {
        newValue = formatter(newValue);
      });
    }
    setValue(newValue);
    if (onChanged) {
      onChanged(newValue);
    }

    if (validator) {
      const validationError = validator(newValue);
      setError(validationError);
    } else {
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmitPressed) {
      onSubmitPressed(value);
    }
  };

  useEffect(() => {
    if (resetState) {
      setError(null);
      setValue(initValue);
    }
  }, [initValue, resetState]);
  const handleOnHover = (hovered: boolean) => {
    setIsHovered(hovered);
  };

  const handleSuffixIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSuffixIconClick && enabled) {
      onSuffixIconClick();
    }
  };

  useEffect(() => {
    if (validator) {
      const validationError = validator(value);
      setError(validationError);
    } else {
      setError(null);
    }
  }, [validator, value]);

  return (
    <div
      className={`bds-input ${className} ${error || focusedError ? 'error-input' : ''} ${!enabled ? 'disabled' : ''}`}
    >
      {labelText && <div className="label-text">{labelText}</div>}
      <button
        type="button"
        className={`row input-container ${isFocused && enabled ? 'focused' : ''} ${
          isHovered && enabled ? 'hovered' : ''
        }`}
        onMouseEnter={() => handleOnHover(true)}
        onMouseLeave={() => handleOnHover(false)}
      >
        <div className="row text-input-container">
          {prefixIcon && <img className="prefix-icon" src={prefixIcon} alt={`${prefixIcon}`} />}
          <input
            type={type}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={hintText}
            disabled={!enabled}
            className="bds-input-field"
            name={name}
            autoComplete={autoComplete}
            onFocus={() => {
              handleOnHover(false);
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              handleOnHover(false);
              if (onBlur) onBlur(value);
            }}
            maxLength={maxLength}
            aria-label={labelText}
          />
          {suffixIcon && (
            <button
              type="button"
              className="suffix-icon-button"
              onClick={handleSuffixIconClick}
              disabled={!enabled}
              aria-label="Suffix icon button"
            >
              <div className="suffix-icon">{suffixIcon}</div>
            </button>
          )}
        </div>
      </button>

      {error ? (
        <div className="error-text">{error}</div>
      ) : (
        helperText && <div className="helper-text">{helperText}</div>
      )}
    </div>
  );
};

export default BdsInput;
